<template>
  <a
    class="side-nav-item"
    :class="{
      'side-nav-item--active': isActive,
      'side-nav-item--child': isChild
    }"
    :href="item.href || item.to || '#'"
    :aria-current="isActive ? 'page' : undefined"
    @click="handleClick"
    @keydown.enter="handleKeyDown"
    @keydown.space.prevent="handleKeyDown"
  >
    <lucide-icon
      :name="item.icon || defaultIcon"
      :size="isChild ? 16 : 18"
      class="side-nav-item__icon"
      aria-hidden="true"
    />
    <span class="side-nav-item__label">
      {{ item.label }}
    </span>
    <span
      v-if="item.badge && !isChild"
      class="side-nav-item__badge"
      :style="{ backgroundColor: item.badgeColor || '#406A94' }"
      :aria-label="`${item.badge} items`"
    >
      {{ item.badge }}
    </span>
  </a>
</template>

<script lang="ts">
import Vue, { PropType } from 'vue';
import LucideIcon from '../LucideIcon';

export interface NavItem {
  label: string;
  icon?: string;
  active?: boolean;
  to?: string;
  href?: string;
  action?: string;
  badge?: string | number;
  badgeColor?: string;
}

export default Vue.extend({
  name: 'SideNavItem',

  components: {
    LucideIcon
  },

  props: {
    item: {
      type: Object as PropType<NavItem>,
      required: true
    },
    isChild: {
      type: Boolean,
      default: false
    },
    isActive: {
      type: Boolean,
      default: false
    },
    defaultIcon: {
      type: String,
      default: 'Circle'
    }
  },

  methods: {
    handleClick(event: MouseEvent) {
      // Prevent default if using # fallback
      if ((!this.item.href && !this.item.to) || (this.item.href === '#' || this.item.to === '#')) {
        event.preventDefault();
      }
      this.$emit('click', event);
    },

    handleKeyDown(event: KeyboardEvent) {
      // Prevent default if using # fallback
      if ((!this.item.href && !this.item.to) || (this.item.href === '#' || this.item.to === '#')) {
        event.preventDefault();
      }
      this.$emit('click', event);
    }
  }
});
</script>

<style lang="scss" scoped>
.side-nav-item {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 6px 8px;
  margin-bottom: 4px;
  font-family: Inter, -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
  font-size: 14px;
  font-weight: 400;
  letter-spacing: -0.006em;
  color: #637083;
  color: var(--muted-foreground, #637083);
  text-decoration: none;
  text-rendering: optimizeLegibility;
  border-radius: 0.375rem;
  cursor: pointer;
  transition: all 150ms;
  position: relative;
  background: transparent;

  &:hover {
    background-color: rgba(0, 0, 0, 0.04) !important;
  }

  &--active {
    background-color: rgba(0, 0, 0, 0.06);
    color: #4b5563;
    color: var(--foreground, #4b5563);
    font-weight: 500;
    border-left: 2px solid #406A94;
    border-left-color: var(--primary, #406A94);
    padding-left: 6px;

    .side-nav-item__icon {
      color: inherit;
      opacity: 1;
    }
  }

  &--child {
    padding-left: 32px;
    font-size: 0.8125rem;
  }

  &__icon {
    flex-shrink: 0;
    color: inherit;
    opacity: 0.7;

    .side-nav-item--active & {
      opacity: 1;
    }
  }

  &__label {
    flex: 1;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  &__badge {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    height: 18px;
    padding: 0 6px;
    font-size: 10px;
    font-weight: 600;
    color: white;
    background-color: #406A94;
    background-color: var(--primary, #406A94);
    border-radius: var(--radius-full);
    margin-left: auto;
  }
}

</style>

<!-- Unscoped styles for dark mode (scoped :global doesn't work in Vue 2) -->
<style lang="scss">
.dark .side-nav-item:hover {
  background-color: rgba(255, 255, 255, 0.04) !important;
}

.dark .side-nav-item--active {
  background-color: rgba(255, 255, 255, 0.06) !important;
}
</style>
